import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FileUpload from './components/FileUpload';
import Questionnaire from './components/Questionnaire';
import Results from './components/Results';
import './App.css';

function App() {
  const [step, setStep] = useState('upload'); // upload, questions, results
  const [filingId, setFilingId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [priorYearData, setPriorYearData] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Initialize a new filing session
    initializeFilingSession();
  }, []);

  const initializeFilingSession = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/filings');
      setFilingId(response.data.filingId);
      setError(null);
    } catch (err) {
      setError('Failed to start a new filing session');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, spouseNumber = 1, spouseName = 'Primary Filer', returnType = 'federal', uploadType = 'prior') => {
    if (!filingId) {
      setError('Filing ID not initialized');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('spouseNumber', spouseNumber);
      formData.append('spouseName', spouseName);

      // Determine endpoint and form data based on upload type
      let endpoint;
      if (uploadType === 'income') {
        endpoint = `/api/filings/${filingId}/income`;
        formData.append('documentType', returnType);
      } else {
        endpoint = `/api/filings/${filingId}/upload`;
        formData.append('returnType', returnType);
      }

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update prior year data with nested structure for federal/state
      const updatedData = priorYearData || { primary: {}, spouse: {} };

      if (uploadType === 'income') {
        // Handle income document uploads
        if (spouseNumber === 1) {
          if (!updatedData.primary) updatedData.primary = {};
          if (!updatedData.primary.income) updatedData.primary.income = {};
          updatedData.primary.income[returnType] = response.data;
        } else {
          if (!updatedData.spouse) updatedData.spouse = {};
          if (!updatedData.spouse.income) updatedData.spouse.income = {};
          updatedData.spouse.income[returnType] = response.data;
        }
      } else {
        // Handle prior year return uploads
        if (spouseNumber === 1) {
          if (!updatedData.primary) updatedData.primary = {};
          updatedData.primary[returnType] = response.data;
        } else {
          if (!updatedData.spouse) updatedData.spouse = {};
          updatedData.spouse[returnType] = response.data;
        }
      }

      setPriorYearData(updatedData);

      // Don't automatically advance - let user upload income docs first if they want
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const proceedToQuestions = () => {
    setStep('questions');
  };

  const handleQuestionsSubmit = async (responses) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/filings/${filingId}/responses`,
        { responses }
      );

      setResults(response.data);
      setStep('results');
      setError(null);
    } catch (err) {
      setError('Failed to submit responses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = () => {
    setStep('upload');
    setUploadedFile(null);
    setPriorYearData(null);
    setResults(null);
    setError(null);
    initializeFilingSession();
  };

  return (
    <div className="App">
      <header className="app-header">
        <h1>Tax Preparer</h1>
        <p>Compare your prior year return and receive filing recommendations</p>
      </header>

      <main className="app-main">
        {error && <div className="error-message">{error}</div>}

        {step === 'upload' && (
          <FileUpload
            onFileUpload={handleFileUpload}
            onProceed={proceedToQuestions}
            loading={loading}
            priorYearData={priorYearData}
          />
        )}

        {step === 'questions' && (
          <Questionnaire
            filingId={filingId}
            onSubmit={handleQuestionsSubmit}
            loading={loading}
            priorYearData={priorYearData}
          />
        )}

        {step === 'results' && (
          <Results
            results={results}
            priorYearData={priorYearData}
            uploadedFile={uploadedFile}
            onStartOver={handleStartOver}
          />
        )}
      </main>

      <footer className="app-footer">
        <p>Tax Preparer © 2026 - For informational purposes only. Consult a tax professional for specific advice.</p>
      </footer>
    </div>
  );
}

export default App;
